import {useTheme} from '@mui/material';
import React, {useState} from 'react';

import {TypeLocalNameEnum} from 'domain/usecases';
import {makeGetExamsTypesLocation} from 'main/factories/usecases';
import {
	DateFormatEnum,
	FeaturesEnum,
	OperationStateTypesEnum,
	TermsOriginEnum,
} from 'presentation/constants';
import {useContractStatus, useHostName, usePrint} from 'presentation/hooks';
import {useGoToRebooking} from 'presentation/hooks/useGoToRebooking';
import {BookingTypeEnum, FlowEnum, ufNameMap} from 'presentation/pages/private/booking/constants';

import {useCallChatRoom, useMediaQueries} from 'presentation/pages/private/hooks';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {
	FlowTypeEnumWithoutAuth,
	formatDateString,
	formatDateWithWeekday,
	getAddressforMaps,
	getUserInfoFromToken,
} from 'presentation/utils';
import {formatGender} from 'presentation/utils/formatGender';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';

import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {UNITS_CHANNELS_CONTACTS} from 'presentation/modules/booking/shared/constants/unitsChannelsContacts';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';
import {useCalendarEvent} from 'presentation/modules/booking/shared/hooks/useCalendarEvent';
import {useMonitoringTime} from 'presentation/pages/private/hooks/useMonitoringTime';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {LocationType} from 'presentation/pages/private/store/locationStore/types';

import {AppointmentsHomeStateType} from 'presentation/pages/private/home/types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useVoiceTranscriptionDrawerStore} from 'presentation/components/VoiceTranscriptionDrawer/store';
import {openGoogleMaps} from 'presentation/modules/booking/shared/utils/openGoogleMaps';
import {isAdvanceDiagnosticsAppointment} from 'presentation/modules/myAppointments/utils/isAdvanceDiagnosticsAppointment';
import {useAdvanceDiagnosticsExamBooking} from 'presentation/pages/private/home/components/AdvanceDiagnosticsBooking/hooks/useAdvanceDiagnosticsExamBooking';

const {rescheduleAppointmentTitle} = translations['pt-br'].privateFlow.myAppointments;

type UseMyAppointmentDetailsContentProps = {
	flowType: FlowTypeEnumWithoutAuth;
	bookedAppointment: AppointmentsHomeStateType;
};

export function useMyAppointmentDetailsContent({
	flowType,
	bookedAppointment,
}: UseMyAppointmentDetailsContentProps) {
	const {colors} = useTheme();
	const {dataHostNameState} = useHostName();
	const accountDataStateRecoil = useAccountDataStore(store => store.accountData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const calendarEvent = useCalendarEvent(bookedAppointment);

	const [isContractSuspendedModalActiveState, setIsContractSuspendedModalActiveState] =
		useState<boolean>(false);
	const [isExamBlockedModalActiveState, setIsExamBlockedModalActiveState] =
		useState<boolean>(false);
	const [isBornWellActiveState, setIsBornWellActiveState] = useState<boolean>(false);
	const [openPhoneDrawerState, setOpenPhoneDrawerState] = useState<boolean>(false);
	const [anchorElState, setAnchorElState] = useState<null | HTMLElement>(null);

	const [operationState, setOperationState] = useState(OperationStateTypesEnum.NONE);
	const isExamsTypesLocationError = operationState === OperationStateTypesEnum.ERROR;
	const isRebookingSubmittingState = operationState === OperationStateTypesEnum.SUBMITTING;

	const {isMobile, isTablet, betweenMobileAndDesktop, isLaptop, largerDevices, isDesktop} =
		useMediaQueries();
	const {isPrinting, printContent, printRef} = usePrint();
	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const {callChatRoom, IS_LOADING: callChatRoomIsLoading} = useCallChatRoom();
	const onOpen = useVoiceTranscriptionDrawerStore(state => state.onOpen);
	const {isEnabled: isUrgencyTeleTermsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_URGENCY_TELE_TERMS,
	);
	const {isEnabled: isAdvanceDiagnosticsExamsJourneyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_PPO_ADVANCE_DIAGNOSTICS_EXAMS_JOURNEY,
	);
	const {isContractSuspended} = useContractStatus();
	const {rebookAdvanceDiagnosticsExam} = useAdvanceDiagnosticsExamBooking();

	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isExam = flowType === FlowEnum.EXAM;
	const isDental = flowType === FlowEnum.ODONTO;
	const isSurgery = flowType === FlowEnum.SURGERY;

	const getLocation = useLocationStore(state => state.getLocation);

	const getLocationTypeFromFlow = (): LocationType =>
		isDental ? FlowType.ODONTO : FlowType.DEFAULT;

	const locationType = getLocationTypeFromFlow();
	const currentAppointmentLocation = getLocation(cdPessoaC, locationType);

	const {iconName} = useBookingTheme({flowType});

	const {goToTelehealthAvailability, dateString} = useMonitoringTime(
		bookedAppointment.dateUtc ?? bookedAppointment.date,
	);

	const {isEnabled: isUploadFileTeleFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_UPLOAD_FILE_TELE,
	);

	const showEnterRoom = isTelehealth && goToTelehealthAvailability;

	const rebookingBtnText = isExam
		? rescheduleAppointmentTitle.exam
		: rescheduleAppointmentTitle.default;

	function handleOpenTelehealthRoom() {
		if (bookedAppointment.telehealthUrl) {
			if (isUrgencyTeleTermsEnabled) {
				onOpen(bookedAppointment.telehealthUrl, TermsOriginEnum.TELEHEALTH);
				return;
			}

			window.open(bookedAppointment.telehealthUrl);
		}
	}

	const {
		clinicName,
		formattedAddress,
		phoneNumbers,
		providerName,
		specialtyName,
		specialtyCode,
		subSpecialtyCode,
		bookingNumber,
		email,
		phone,
		date,
		protocolNumber,
		isConfirmed,
		bookingType,
		cdPrestador,
		unitCode,
	} = bookedAppointment;

	const isAdvanceDiagnosticsExam = isAdvanceDiagnosticsAppointment({
		appointment: {bookingType, unitCode},
		isAdvanceDiagnosticsExamsJourneyEnabled,
	});

	const dateStringFormatted = formatDateString(
		dateString,
		DateFormatEnum.DATE_FORMAT_9,
		DateFormatEnum.DATE_FORMAT_1,
	);
	const appointmentDate = isTelehealth ? dateStringFormatted : date;

	const {
		day,
		month: monthName,
		formatted,
	} = formatDateWithWeekday(appointmentDate, DateFormatEnum.DATE_FORMAT_1);

	const splittedAddress = formattedAddress.split(' - ');
	const [cityCode, ufCode] = splittedAddress[splittedAddress.length - 1].split('/');
	const ufName = ufNameMap[ufCode.toUpperCase()];

	const address = getAddressforMaps(formattedAddress);

	const handleOpenGoogleMaps = () => openGoogleMaps(address.addressForMaps || '');

	const getBookingTypeAndOrigin = () => {
		if (bookingType === BookingTypeEnum.TELEHEALTH) return BookingOriginEnum.BOOKING_TELEHEALTH;

		if (bookingType === BookingTypeEnum.EXAM) return BookingOriginEnum.BOOKING_EXAM;

		if (bookingType === BookingTypeEnum.ODONTO) return BookingOriginEnum.BOOKING_ODONTO;

		return BookingOriginEnum.BOOKING_HEALTH;
	};

	const {goToRebooking} = useGoToRebooking({
		specialtyName,
		specialtyCode,
		subSpecialtyCode: subSpecialtyCode || '',
		bookingNumber,
		bookingType,
		ufCode,
		cityCode,
		ufName,
		email,
		phone,
		flowType,
		isTelehealth,
		currentAppointmentLocation,
		locationType,
		setOperationState: (state: string) => setOperationState(state as OperationStateTypesEnum),
		setIsBornWellActiveState,
		setIsContractSuspendedModalActiveState,
		getBookingTypeAndOrigin,
		setActiveBookingFlowStep,
	});

	const handleRebookingExamFlow = async () => {
		try {
			setOperationState(OperationStateTypesEnum.SUBMITTING);

			const response = await makeGetExamsTypesLocation().get({
				stateCode: ufCode,
				cityName: cityCode.toUpperCase(),
				age: accountDataStateRecoil.beneficiary.idadeC,
				gender: formatGender(accountDataStateRecoil.beneficiary.flSexoUsuarioC),
				typeLocalName: TypeLocalNameEnum.CIDADE,
			});

			const examList = response?.tiposExames ?? [];
			const examBySpecialtyCode = examList.find(item => item?.cdTipoExame === specialtyCode);

			if (!examBySpecialtyCode) {
				setIsExamBlockedModalActiveState(true);
				setOperationState(OperationStateTypesEnum.DONE);
				return;
			}

			setOperationState(OperationStateTypesEnum.DONE);
			goToRebooking();
		} catch (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const handleRebookingAdvanceDiagnostics = () => {
		if (isContractSuspended) {
			setIsContractSuspendedModalActiveState(true);
			return;
		}

		rebookAdvanceDiagnosticsExam({
			specialtyCode,
			specialtyName,
			bookingNumber,
			email,
			phone,
		});
	};

	const getRebookingHandler = () => {
		if (isAdvanceDiagnosticsExam) return handleRebookingAdvanceDiagnostics;

		return isExam ? handleRebookingExamFlow : goToRebooking;
	};

	const handleRebooking = getRebookingHandler();

	const isRebookingLoading = isRebookingSubmittingState || isBornWellActiveState;

	const listPhoneNumbers = UNITS_CHANNELS_CONTACTS[dataHostNameState.product].phones;
	const listPhoneNumbersAlert = UNITS_CHANNELS_CONTACTS[dataHostNameState.product].alert;

	const isPhoneNumberMenuOpen = Boolean(anchorElState);

	const hideProtocolCard = isTelehealth || isSurgery;

	const handleOpenPhoneMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isMobile) {
			setOpenPhoneDrawerState(true);
			return;
		}
		setAnchorElState(event.currentTarget);
	};

	const handleTogglePhoneDrawer = () => {
		setOpenPhoneDrawerState(prev => !prev);
	};

	const handleClosePhoneMenu = () => {
		setAnchorElState(null);
	};

	return {
		colors,
		isContractSuspendedModalActiveState,
		setIsContractSuspendedModalActiveState,
		isExamBlockedModalActiveState,
		setIsExamBlockedModalActiveState,
		isBornWellActiveState,
		setIsBornWellActiveState,
		openPhoneDrawerState,
		setOpenPhoneDrawerState,
		anchorElState,
		setAnchorElState,
		operationState,
		isExamsTypesLocationError,
		isRebookingSubmittingState,
		isMobile,
		isTablet,
		betweenMobileAndDesktop,
		isLaptop,
		largerDevices,
		isDesktop,
		isPrinting,
		printContent,
		printRef,
		callChatRoom,
		callChatRoomIsLoading,
		isTelehealth,
		isExam,
		isDental,
		isSurgery,
		currentAppointmentLocation,
		iconName,
		showEnterRoom,
		rebookingBtnText,
		clinicName,
		formattedAddress,
		phoneNumbers,
		providerName,
		cdPrestador,
		specialtyName,
		specialtyCode,
		subSpecialtyCode,
		bookingNumber,
		email,
		phone,
		date,
		protocolNumber,
		isConfirmed,
		bookingType,
		day,
		monthName,
		formatted,
		ufName,
		handleOpenGoogleMaps,
		handleRebooking,
		isRebookingLoading,
		listPhoneNumbers,
		listPhoneNumbersAlert,
		isPhoneNumberMenuOpen,
		handleOpenPhoneMenu,
		handleTogglePhoneDrawer,
		handleClosePhoneMenu,
		calendarEvent,
		hideProtocolCard,
		handleOpenTelehealthRoom,
		setOperationState,
		isUploadFileTeleFeatureFlagEnabled,
		isAdvanceDiagnosticsExam,
	};
}
